import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Paper,
  alpha,
} from '@mui/material';
import { FaReact, FaNodeJs } from "react-icons/fa";
import { SiMongodb } from "react-icons/si";
import BaseTab from './BaseTab';

interface FrontendTechStackTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  lokiGradient: string;
  goldColor: string;
  isMobile: boolean;
}

const FrontendTechStackTab: React.FC<FrontendTechStackTabProps> = (props) => {
  return (
    <BaseTab
      {...props}
      title="Frontend Technology Stack"
      emoji="🛠"
    >
      <Typography variant="h6" sx={{ color: props.goldColor, mb: 3 }}>
        Modern Technology Stack
      </Typography>
      
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%', bgcolor: alpha(props.goldColor, 0.1) }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FaReact size={40} color={props.goldColor} />
                <Box sx={{ ml: 2 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Frontend Architecture
                  </Typography>
                  <Typography variant="caption" color={props.colors.textSecondary}>
                    React 19.2.0 + TypeScript 5.6.2
                  </Typography>
                </Box>
              </Box>
              <List dense>
                {[
                  { name: 'Vite 5.4.8', desc: 'Build Tool' },
                  { name: 'Material-UI 7.3.6', desc: 'Component Library' },
                  { name: 'Tailwind CSS', desc: 'Utility Classes' },
                  { name: 'Framer Motion', desc: 'Animations' },
                  { name: 'React Query 5.90.12', desc: 'Data Fetching' },
                  { name: 'React Router 7.10.1', desc: 'Navigation' },
                ].map((tech, idx) => (
                  <ListItem key={idx} sx={{ py: 1 }}>
                    <ListItemText
                      primary={tech.name}
                      secondary={tech.desc}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip label="v1" size="small" />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%', bgcolor: alpha('#0F3B2E', 0.1) }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FaNodeJs size={40} color="#0F3B2E" />
                <Box sx={{ ml: 2 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Backend Architecture
                  </Typography>
                  <Typography variant="caption" color={props.colors.textSecondary}>
                    Separate Repository
                  </Typography>
                </Box>
              </Box>
              <List dense>
                {[
                  { name: 'Node.js + Express.js', desc: 'Runtime & Framework' },
                  { name: 'MongoDB + Mongoose', desc: 'Database & ODM' },
                  { name: 'JWT Authentication', desc: 'Security' },
                  { name: 'Google OAuth 2.0', desc: 'Social Login' },
                  { name: 'WebSocket', desc: 'Real-time Features' },
                  { name: 'Pinata (IPFS)', desc: 'File Storage' },
                ].map((tech, idx) => (
                  <ListItem key={idx} sx={{ py: 1 }}>
                    <ListItemText
                      primary={tech.name}
                      secondary={tech.desc}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip label="v1" size="small" />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
          State Management
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha(props.goldColor, 0.1) }}>
              <Typography variant="subtitle2" fontWeight={700} color={props.goldColor}>
                Context API
              </Typography>
              <Typography variant="caption" color={props.colors.textSecondary}>
                Authentication & Theme State
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#0F3B2E', 0.1) }}>
              <Typography variant="subtitle2" fontWeight={700} color="#0F3B2E">
                React Query
              </Typography>
              <Typography variant="caption" color={props.colors.textSecondary}>
                Server State Management
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha('#1D5C4A', 0.1) }}>
              <Typography variant="subtitle2" fontWeight={700} color="#1D5C4A">
                Local Storage
              </Typography>
              <Typography variant="caption" color={props.colors.textSecondary}>
                Persistent User Preferences
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </BaseTab>
  );
};

export default FrontendTechStackTab;